import { useMemo, useState, type FormEvent } from "react";

import { Button, Icon, type IconName, Modal, Select } from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import { invitationService } from "@/services/invitationService";
import { useInvalidateInvitations } from "@/hooks/useInvitations";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useClients } from "@/hooks/useClients";
import { useRoles } from "@/hooks/useRoles";
import type { GrantScope, InvitationCreated, InvitationType } from "@/types/invitation";
import "./InviteModal.css";

const SCOPE_OPTIONS: { value: GrantScope; icon: IconName; title: string; desc: string }[] = [
  {
    value: "workspace",
    icon: "building",
    title: "Workspace inteiro",
    desc: "Acesso a todas as orgs e empresas atuais e futuras.",
  },
  {
    value: "organization",
    icon: "layers",
    title: "Organizações",
    desc: "Acesso só a organizações específicas.",
  },
  {
    value: "client",
    icon: "briefcase",
    title: "Empresas",
    desc: "Acesso só a CNPJs específicos.",
  },
];

const TITLES: Record<InvitationType, string> = {
  user: "Convidar usuário",
  organization: "Convidar organização",
  client: "Convidar empresa",
  workspace: "Convidar workspace",
};

type Props = {
  kind: InvitationType;
  workspaceId: number;
  onClose: () => void;
};

export function InviteModal({ kind, workspaceId, onClose }: Props) {
  const invalidate = useInvalidateInvitations(workspaceId);
  const { data: orgs = [] } = useOrganizations(
    kind === "client" || kind === "user" ? workspaceId : null,
  );
  const { data: clients = [] } = useClients(kind === "user" ? workspaceId : null);
  const { data: roles = [] } = useRoles(kind === "user" ? workspaceId : null);

  const [email, setEmail] = useState("");
  const [roleSlug, setRoleSlug] = useState("analyst");
  const [scope, setScope] = useState<GrantScope>("workspace");
  const [orgIds, setOrgIds] = useState<number[]>([]);
  const [clientIds, setClientIds] = useState<number[]>([]);
  const [suggestion, setSuggestion] = useState("");
  const [organizationId, setOrganizationId] = useState<number | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<InvitationCreated | null>(null);
  const [copied, setCopied] = useState(false);

  const canSubmit = useMemo(() => {
    if (!email.trim()) return false;
    if (kind === "client" && organizationId === "") return false;
    if (kind === "user" && scope === "organization" && orgIds.length === 0) return false;
    if (kind === "user" && scope === "client" && clientIds.length === 0) return false;
    return true;
  }, [email, kind, organizationId, scope, orgIds, clientIds]);

  function toggle(list: number[], id: number): number[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      let result: InvitationCreated;
      if (kind === "user") {
        result = await invitationService.createUser(workspaceId, {
          email: email.trim(),
          role_slug: roleSlug,
          scope,
          organization_ids: scope === "organization" ? orgIds : [],
          client_ids: scope === "client" ? clientIds : [],
        });
      } else if (kind === "organization") {
        result = await invitationService.createOrg(workspaceId, {
          email: email.trim(),
          organization_name_suggestion: suggestion.trim() || undefined,
        });
      } else if (kind === "client") {
        result = await invitationService.createClient(workspaceId, {
          email: email.trim(),
          organization_id: organizationId as number,
          client_name_suggestion: suggestion.trim() || undefined,
        });
      } else {
        result = await invitationService.createWorkspace({
          email: email.trim(),
          workspace_name_suggestion: suggestion.trim() || undefined,
        });
      }
      invalidate();
      setCreated(result);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!created) return;
    await navigator.clipboard.writeText(created.accept_url);
    setCopied(true);
  }

  if (created) {
    return (
      <Modal
        onClose={onClose}
        title="Convite criado"
        footer={
          <Button variant="primary" onClick={onClose}>
            Concluir
          </Button>
        }
      >
        <p className="td-body">
          Envie este link mágico para <strong>{created.email_masked}</strong>. O e-mail automático
          chega quando o módulo de notificações for habilitado.
        </p>
        <div className="invite-link-box">
          <code>{created.accept_url}</code>
          <Button variant="ghost" iconLeft={copied ? "check" : "copy"} onClick={() => void copyLink()}>
            {copied ? "Copiado" : "Copiar"}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      onClose={onClose}
      title={TITLES[kind]}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="invite-form" disabled={busy || !canSubmit}>
            {busy ? "Enviando…" : "Criar convite"}
          </Button>
        </>
      }
    >
      <form
        id="invite-form"
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {error && <div className="form-error">{error}</div>}

        <label className="form-field">
          <span>E-mail do convidado</span>
          <input
            className="form-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>

        {kind === "user" && (
          <>
            <label className="form-field">
              <span>Papel (role)</span>
              <Select
                value={roleSlug}
                onChange={setRoleSlug}
                options={roles.map((r) => ({ value: r.slug, label: r.name }))}
              />
            </label>
            <div className="form-field">
              <span>Escopo de acesso</span>
              <div className="scope-cards">
                {SCOPE_OPTIONS.map((s) => (
                  <button
                    type="button"
                    key={s.value}
                    className={`scope-card ${scope === s.value ? "active" : ""}`}
                    onClick={() => setScope(s.value)}
                  >
                    <Icon name={s.icon} size={18} />
                    <span className="scope-card-title">{s.title}</span>
                    <span className="scope-card-desc">{s.desc}</span>
                  </button>
                ))}
              </div>
            </div>
            {scope === "organization" && (
              <fieldset className="invite-checklist">
                <legend>Organizações cobertas</legend>
                {orgs.map((o) => (
                  <label key={o.id}>
                    <input
                      type="checkbox"
                      checked={orgIds.includes(o.id)}
                      onChange={() => setOrgIds((l) => toggle(l, o.id))}
                    />
                    {o.nickname ?? o.legal_name}
                  </label>
                ))}
                {orgs.length === 0 && <p className="td-small">Nenhuma organização cadastrada.</p>}
              </fieldset>
            )}
            {scope === "client" && (
              <fieldset className="invite-checklist">
                <legend>Empresas cobertas</legend>
                {clients.map((c) => (
                  <label key={c.id}>
                    <input
                      type="checkbox"
                      checked={clientIds.includes(c.id)}
                      onChange={() => setClientIds((l) => toggle(l, c.id))}
                    />
                    {c.legal_name}
                  </label>
                ))}
                {clients.length === 0 && <p className="td-small">Nenhuma empresa cadastrada.</p>}
              </fieldset>
            )}
          </>
        )}

        {kind === "client" && (
          <label className="form-field">
            <span>Organização</span>
            <Select
              value={organizationId === "" ? null : String(organizationId)}
              onChange={(v) => setOrganizationId(v === "" ? "" : Number(v))}
              options={orgs.map((o) => ({
                value: String(o.id),
                label: o.nickname ?? o.legal_name,
              }))}
              placeholder="Selecione…"
            />
          </label>
        )}

        {kind !== "user" && (
          <label className="form-field">
            <span>
              {kind === "workspace"
                ? "Nome sugerido do workspace"
                : kind === "organization"
                  ? "Nome sugerido da organização"
                  : "Nome sugerido da empresa"}
            </span>
            <input
              className="form-input"
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
            />
          </label>
        )}
      </form>
    </Modal>
  );
}
